import React, { useState, useEffect, useContext } from "react"; // Import React and hooks
import { API_URL } from "../components/api"; // Import the API URL for backend requests
import Modal from "react-bootstrap/Modal"; // Import Modal component from React-Bootstrap
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridFooterContainer,
  GridPagination,
} from "@mui/x-data-grid"; // Import Material-UI DataGrid components
import ExcelSVG from "../../ExcelSVG.svg"; // Import Excel icon for export button
import FilterSVG from "../../FilterSVG.svg";
import Button from "react-bootstrap/Button"; // Import Button component from React-Bootstrap
import * as XLSX from "xlsx"; // Import XLSX for Excel export functionality
import AuthContext from "../context/AuthContext"; // Import AuthContext for user authentication
import EditLabels from "./EditLabels"; // Import EditApartment component for editing apartments
import axios from "axios"; // Import axios for making API requests

function LabelsTable() {
  // State to store the list of apartments, search query, and loading state
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [showFullDetails, setShowFullDetails] = useState(false); // State for displaying full details modal

  // Functions to control modals
  const handleshowDetails = () => setShow(true);
  const handlecloseDetails = () => setShow(false);
  const handleshowFullDetails = () => setShowFullDetails(true);
  const handlecloseFullDetails = () => setShowFullDetails(false);

  const { user } = useContext(AuthContext); // Get user info from context
  const authTokens = JSON.parse(localStorage.getItem("authTokens")); // Get authentication tokens from local storage

  // State for storing full details of an apartment to be edited
  const [fullDetails, setFullDetails] = useState({
    label_id: 0,
    label_name: "",
    label_description: "",
  });

  // Function to update full details when editing an apartment
  const updateFullDetails = (fullDetails) => {
    setFullDetails(fullDetails);
  };

  const [filters, setFilters] = useState({
    label_name: "",
    label_description: "",
  });
  const [showFilterModal, setShowFilterModal] = useState(false);
  // State for filtered rows data
  const [gridFilterModel, setGridFilterModel] = useState({
    items: [],
  });

  const [searchInput, setSearchInput] = useState(""); // To capture search input

  // NEW: State to track column visibility
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [searchFilterTimeout, setSearchFilterTimeout] = useState(null);

  // Function to fetch apartment data from the API
  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/administration/labels/`, {
        headers: {
          Authorization: `Bearer ${authTokens.access}`, // Authorization header with token
        },
        params: {
          username: user.username, // Include username in request params
        },
      });

      if (response.status === 200) {
        setData(response.data.reverse()); // Set fetched data in state
        console.log(response.data.reverse()); // Log the data for debugging
      }
    } catch (error) {
      console.error(error); // Log any error
    } finally {
      setLoading(false); // Set loading state to false once data is fetched
    }
  };

  // useEffect hook to fetch data on component mount or when user/username changes
  useEffect(() => {
    fetchData();
  }, [user.username, authTokens.access]);

  // Define columns for DataGrid component
  const columns = [
    { field: "label_name", headerName: "Name", flex: 2, minWidth: 150 },

    {
      field: "label_description",
      headerName: "Description",
      minWidth: 230,
    },

    {
      field: "actions",
      headerName: "Actions",
      minWidth: 230,
      flex: 2,
      renderCell: (params) => (
        <div>
          <button
            className="edit-client"
            onClick={() => {
              const fullDetails = {
                label_id: params.row.label_id,
                label_name: params.row.label_name,
                label_description: params.row.label_description,
              };
              updateFullDetails(fullDetails); // Update details to be edited
              handleshowFullDetails(); // Show full details modal
            }}
          >
            Edit
          </button>
        </div>
      ),
    },
  ];

  // Function to get the row ID for DataGrid
  const getRowId = (row) => row.label_id;

  // Create a delayed search filter function
  const delaySearchFilter = (input) => {
    setSearchQuery(input);
  };

  // useEffect hook to manage session storage for search functionality
  useEffect(() => {
    const searchValue = sessionStorage.getItem("searchLabel");
    Object.keys(sessionStorage).forEach((key) => {
      if (key !== "searchLabel") {
        sessionStorage.removeItem(key); // Clear all session storage except searchApartment
      }
    });
    if (searchValue) {
      sessionStorage.setItem("searchLabel", searchValue);
      setSearchQuery(searchValue); // Restore search query if available
    }
  }, []);

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchInput(query);
    sessionStorage.setItem("searchLabel", query);

    if (searchFilterTimeout) {
      clearTimeout(searchFilterTimeout);
    }

    const newTimeout = setTimeout(() => {
      delaySearchFilter(query);
    }, 100);
    setSearchFilterTimeout(newTimeout);
  };

  // IMPROVED: Combined filtering logic that works for both display AND export
  const filteredRows = data.filter((row) => {
    // Apply search query filter
    const searchMatch = searchQuery
      .split(" ")
      .every((word) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(word.toLowerCase().trim())
        )
      );

    // Apply modal filters
    const modalFilterMatch = Object.keys(filters).every((key) => {
      if (!filters[key]) return true;

      if (key === "connectivity_dependent") {
        const filterValue = filters[key].toLowerCase();
        if (row.connectivity === "Fiber") {
          return (row.code || "").toLowerCase().includes(filterValue);
        } else if (row.connectivity === "Wireless") {
          return (row.name || "").toLowerCase().includes(filterValue);
        }
        return false;
      }

      const rowValue = String(row[key] || "").toLowerCase();
      const filterValue = String(filters[key]).toLowerCase();
      return rowValue.includes(filterValue);
    });

    // Apply DataGrid filters
    const gridFilterMatch = gridFilterModel.items.every((filterItem) => {
      if (!filterItem.value) return true;

      const cellValue = row[filterItem.field];
      const filterValue = filterItem.value;

      switch (filterItem.operator) {
        case "contains":
          return String(cellValue || "")
            .toLowerCase()
            .includes(String(filterValue).toLowerCase());
        case "equals":
          return String(cellValue || "") === String(filterValue);
        case "startsWith":
          return String(cellValue || "")
            .toLowerCase()
            .startsWith(String(filterValue).toLowerCase());
        case "endsWith":
          return String(cellValue || "")
            .toLowerCase()
            .endsWith(String(filterValue).toLowerCase());
        default:
          return true;
      }
    });

    return searchMatch && modalFilterMatch && gridFilterMatch;
  });

  const handleFilterChange = (field, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  // Column headers mapping for export
  const columnHeaders = {
    label_name: "Label Name",
    label_description: "Label Description",
  };

  // Format date fields properly for export — e.g. "21/02/2025 10:25:52 AM"
  const formatDateForExport = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);

    // Format as DD/MM/YYYY HH:MM:SS AM/PM
    const formattedDate = date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    // Ensure consistent spacing before AM/PM
    return formattedDate.replace(" am", " AM").replace(" pm", " PM");
  };

  /// Corrected: Transform rows respecting column visibility
  const transformRowsForExport = (rows) => {
    return rows.map((row) => {
      const transformedRow = {};

      Object.keys(columnHeaders).forEach((field) => {
        // Only export if column is visible
        if (columnVisibilityModel[field] !== false) {
          const headerName = columnHeaders[field];

          // Default: use the value or empty string
          transformedRow[headerName] = row[field] ?? "";
        }
      });

      return transformedRow;
    });
  };

  // IMPROVED: Export function that uses the already-filtered rows
  const handleExportToExcel = () => {
    console.log("Exporting data...");
    console.log("Total data rows:", data.length);
    console.log("Filtered rows:", filteredRows.length);
    console.log("Active filters:", {
      search: searchQuery,
      modalFilters: filters,
      gridFilters: gridFilterModel.items,
    });
    console.log("Column visibility:", columnVisibilityModel);

    // Always use filteredRows which has ALL filters applied
    const transformedRows = transformRowsForExport(filteredRows);

    console.log("Rows to export:", transformedRows.length);

    const worksheet = XLSX.utils.json_to_sheet(transformedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Labels");
    XLSX.writeFile(workbook, "Labels.xlsx"); // Download Excel file
  };

  const [isPressed, setIsPressed] = useState(false);

  const handleExportClick = () => {
    setIsPressed(true);
    handleExportToExcel();
    setTimeout(() => {
      setIsPressed(false);
    }, 200);
  };

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <Button
        onClick={() => setShowFilterModal(true)}
        variant="outline-primary"
        style={{
          border: "none",
        }}
      >
        <img
          src={FilterSVG}
          alt="Filter Icon"
          style={{
            color: "white",
            width: "18px",
            marginRight: "8px",
            filter: "invert(0)", // keep black icon
          }}
        />
        Filter
      </Button>
      {/*<GridToolbarFilterButton />*/}
      <GridToolbarExport />
      <Button
        onClick={handleExportClick}
        variant="outline-success"
        style={{
          display: "flex",
          alignItems: "center",
          padding: "5px 10px",
          fontSize: "14px",
          border: "none",
          backgroundColor: isPressed ? "white" : "transparent",
          color: isPressed ? "black" : "#007bff",
          cursor: "pointer",
        }}
      >
        <img
          src={ExcelSVG}
          alt="Excel Icon"
          style={{
            width: "20px",
            marginRight: "8px",
            filter: isPressed ? "invert(0)" : "invert(1)",
          }}
        />
        Excel
      </Button>
    </GridToolbarContainer>
  );

  // Custom footer for DataGrid with pagination
  const CustomFooter = () => (
    <GridFooterContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          padding: "10px",
        }}
      >
        <GridPagination />
      </div>
    </GridFooterContainer>
  );

  return (
    <div className="ClientTable">
      <style>{`
        .MuiDataGrid-panelWrapper {
          background: #122632 !important;
        }
      `}</style>
      <input
        type="text"
        placeholder="Search..."
        value={searchInput}
        onChange={handleSearchChange}
        className="search-client"
      />
      {!loading && data.length > 0 && (
        <div style={{ height: 520, width: "100%" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={getRowId}
            filterModel={gridFilterModel}
            onFilterModelChange={(newModel) => setGridFilterModel(newModel)}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            components={{
              Footer: () => <CustomFooter />,
              Toolbar: CustomToolbar,
            }}
            rowsPerPageOptions={[10, 100, 1000]}
            pagination
            disableSelectionOnClick
          />
        </div>
      )}

      <Modal
        show={showFullDetails}
        onHide={handlecloseFullDetails}
        className="modal-lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Building</Modal.Title>
        </Modal.Header>
        <div className="Subform">
          <EditLabels
            label_id={fullDetails.label_id}
            label_name={fullDetails.label_name}
            label_description={fullDetails.label_description}
          />
        </div>
      </Modal>
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Filters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {columns
            .filter((col) => col.field !== "actions")
            .map((col) => (
              <div key={col.field} className="filter">
                <label>{col.headerName}</label>
                <input
                  type="text"
                  placeholder={`Filter by ${col.headerName}`}
                  value={filters[col.field] || ""}
                  onChange={(e) =>
                    handleFilterChange(col.field, e.target.value)
                  }
                  className="filter-input"
                />
              </div>
            ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFilterModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default LabelsTable;
